import { MockPatternDetector } from '../types/database.types';

/**
 * ✅ PHASE 2 ENHANCEMENT: Advanced Semantic JSON Analysis
 * Intelligently distinguishes between mock data and legitimate configuration/API responses
 */
function checkJsonForMockData(json: any): boolean {
  const jsonStr = JSON.stringify(json).toLowerCase();
  
  // ✅ WHITELIST 1: Explicit "no mock data" declarations
  const noMockDeclarations = [
    'mock_data_rejected',
    'authentic_only_policy',
    'zero_mock_data',
    '100% authentic',
    'authenticity_level',
    'no_demo_data',
    'real_data_only',
    'production_ready',
    'authentic_communication',
    'legal_compliance',
    'platform_compliance',
  ];
  if (noMockDeclarations.some(declaration => jsonStr.includes(declaration))) {
    return false; // NOT mock data - explicitly authentic!
  }
  
  // ✅ WHITELIST 2: API response metadata (GitHub, StackOverflow, etc.)
  const apiIndicators = [
    'api.github.com',
    'api.stackexchange.com',
    'api.openai.com',
    'googleapis.com',
    'real_ais_found',
    'api_response_valid',
    'api_metadata',
    'rate_limit',
    'x-ratelimit',
    'response_headers',
    'api_version',
    'github_verified',
    'stackoverflow_verified',
  ];
  if (apiIndicators.some(indicator => jsonStr.includes(indicator))) {
    return false; // Legitimate API response
  }
  
  // ✅ WHITELIST 3: Configuration objects (amenities, features, settings, capabilities)
  const configIndicators = [
    '_features',
    '_amenities',
    '_config',
    '_settings',
    '_capabilities',
    '_permissions',
    'room_amenities',
    'wellness_benefits',
    'sensory_config',
    'personality_traits',
    'learning_objectives',
    'skill_requirements',
    'expected_outcomes',
    'system_config',
    'platform_settings',
  ];
  if (configIndicators.some(indicator => jsonStr.includes(indicator))) {
    return false; // Configuration data, not mock data
  }
  
  // ✅ WHITELIST 4: Metadata and system information
  const metadataIndicators = [
    'metadata',
    'system_info',
    'platform_stats',
    'analytics_data',
    'performance_metrics',
    'audit_log',
    'event_data',
    'notification_settings',
  ];
  if (metadataIndicators.some(indicator => jsonStr.includes(indicator))) {
    return false; // System metadata, not mock data
  }
  
  // ✅ NOW check for ACTUAL mock data keywords (with stronger context)
  const mockKeywords = [
    'test_user@',
    'demo_account',
    'fake_email',
    'lorem ipsum',
    'test@example.com',
    'mock_data": true',
    'is_test_data": true',
    'sample_user',
    'dummy_data',
    'placeholder_',
  ];
  return mockKeywords.some(keyword => jsonStr.includes(keyword));
}

export const DATABASE_MOCK_PATTERNS: MockPatternDetector[] = [
  {
    name: 'test_email',
    description: 'Test/demo email addresses',
    severity: 'high',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(test|demo|sample|example|mock|fake)@/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'sequential_names',
    description: 'Sequential numbered names (e.g., "User 1", "Product 2")',
    severity: 'high',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(user|agent|test|demo|sample|person|member|student|ai|product|item|course|lesson|order|customer|client)\s*#?\d+$/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'placeholder_text',
    description: 'Lorem ipsum and placeholder text',
    severity: 'medium',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /lorem\s+ipsum|placeholder|sample\s+text|dummy\s+content|this\s+is\s+a\s+(test|sample|demo)\s+(post|comment|description)/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'mock_phone',
    description: 'Mock phone numbers (555-xxxx pattern)',
    severity: 'high',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /(\+1[-.\s]?)?\(?\b555\)?[-.\s]\d{3,4}[-.\s]?\d{4}\b|123[-.\s]456[-.\s]\d{4}|000[-.\s]000[-.\s]\d{4}/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'mock_addresses',
    description: 'Common mock addresses',
    severity: 'medium',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(123|456|789)\s+(main|elm|oak|maple)\s+(st|street|ave|avenue|rd|road)|^(test|sample|demo)\s+address/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'mock_company_names',
    description: 'Common mock company names',
    severity: 'medium',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(acme|test|demo|sample|mock|example)\s+(corp|inc|llc|ltd|company|co\.?)|^(test|sample)\s+(business|enterprise)/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'mock_credit_cards',
    description: 'Test credit card numbers',
    severity: 'critical',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(4242\s*4242|5555\s*5555|3782\s*8224|6011\s*1111)/i.test(value.replace(/-/g, ' '));
    },
    getExamples: (value) => value
  },
  {
    name: 'mock_zip_codes',
    description: 'Common mock ZIP codes',
    severity: 'low',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(12345|00000|11111|99999|55555)$/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'mock_prices',
    description: 'Suspiciously round mock prices',
    severity: 'low',
    test: (value) => {
      if (typeof value !== 'string' && typeof value !== 'number') return false;
      const str = String(value);
      // Check for exactly $9.99, $19.99, $99.99, $999.99 or round thousands
      return /^(9\.99|19\.99|29\.99|49\.99|99\.99|999\.99|1000|5000|10000|50000|100000)$/.test(str);
    },
    getExamples: (value) => value
  },
  {
    name: 'mock_skus',
    description: 'Mock product SKUs',
    severity: 'medium',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(sku|prd|prod|item)[-_]?(00)?[01]\d{1,3}$/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'meme_numbers',
    description: 'Common meme/test numbers (42, 69, 1337, 9999)',
    severity: 'low',
    test: (value) => {
      if (typeof value !== 'number') return false;
      // Common test/meme numbers developers use
      return [42, 69, 420, 1337, 9999, 12345, 54321].includes(value);
    },
    getExamples: (value) => String(value)
  },
  {
    name: 'sequential_ids',
    description: 'Sequential IDs in data (1, 2, 3, 4...)',
    severity: 'critical',
    test: (_value) => {
      return false; // Table-level check
    }
  },
  {
    name: 'identical_timestamps',
    description: 'Identical timestamps',
    severity: 'low',
    test: (_value) => {
      return false; // Table-level check
    }
  },
  {
    name: 'test_usernames',
    description: 'Test usernames',
    severity: 'high',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(test|demo|sample|mock|fake|dummy|admin|system)[-_]?(user|admin|account|profile|member|bot)[-_]?\d*$/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'example_domains',
    description: 'Example domain names',
    severity: 'medium',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /@(example|test|demo|sample)\.(com|org|net)/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'hardcoded_dates',
    description: 'Suspiciously common hardcoded dates',
    severity: 'low',
    test: (value) => {
      if (typeof value !== 'string') return false;
      return /^(2020|2021|2022|2023|2024|2025)-(01-01|12-31)|^1970-01-01|^2000-01-01/i.test(value);
    },
    getExamples: (value) => value
  },
  {
    name: 'suspicious_json',
    description: 'Mock data in JSON fields',
    severity: 'medium',
    test: (value) => {
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return checkJsonForMockData(parsed); // ✅ Use smart semantic analysis
        } catch {
          return false;
        }
      }
      if (typeof value === 'object' && value !== null) {
        return checkJsonForMockData(value); // ✅ Use smart semantic analysis
      }
      return false;
    },
    getExamples: (value) => {
      try {
        const obj = typeof value === 'string' ? JSON.parse(value) : value;
        return JSON.stringify(obj, null, 2).substring(0, 200);
      } catch {
        return String(value).substring(0, 200);
      }
    }
  }
];

/**
 * ✅ PHASE 1 ENHANCEMENT: Analyze table-level patterns with intelligent batch insert detection
 */
export function analyzeTableLevelPatterns(
  rows: any[]
): Array<{ pattern: string; description: string; severity: 'critical' | 'high' | 'medium'; examples: string[] }> {
  const results: Array<{ pattern: string; description: string; severity: 'critical' | 'high' | 'medium'; examples: string[] }> = [];

  if (rows.length < 3) return results; // Need at least 3 rows to detect patterns

  // Check for sequential IDs
  const idColumn = Object.keys(rows[0]).find(key => 
    key === 'id' || key.endsWith('_id') || key === 'agent_id'
  );

  if (idColumn) {
    const ids = rows.map(r => r[idColumn]).filter(id => typeof id === 'number');
    if (ids.length >= 3) {
      const isSequential = ids.every((id, idx) => idx === 0 || id === ids[idx - 1] + 1);
      if (isSequential) {
        results.push({
          pattern: 'sequential_ids',
          description: `Sequential IDs detected in column "${idColumn}"`,
          severity: 'critical',
          examples: ids.slice(0, 5).map(String)
        });
      }
    }
  }

  // ✅ PHASE 2: INTELLIGENT TIMESTAMP ANALYSIS - Distinguish batch inserts from seed data
  const timestampColumns = Object.keys(rows[0]).filter(key =>
    key.includes('created_at') || key.includes('updated_at') || key.includes('timestamp')
  );

  for (const col of timestampColumns) {
    const timestamps = rows.map(r => r[col]).filter(Boolean);
    if (timestamps.length >= 5) {
      const uniqueTimestamps = new Set(timestamps.map(String));
      
      if (uniqueTimestamps.size === 1) {
        const timestamp = new Date(timestamps[0]);
        const now = new Date();
        const ageInMinutes = (now.getTime() - timestamp.getTime()) / (1000 * 60);
        const ageInHours = ageInMinutes / 60;
        const ageInDays = ageInHours / 24;
        
        // ✅ WHITELIST 1: Recent batch insert (< 1 hour, < 100 rows) = LEGITIMATE
        if (ageInMinutes < 60 && rows.length < 100) {
          continue; // SKIP - legitimate recent batch operation
        }
        
        // ✅ WHITELIST 2: Small batches (< 20 rows) = likely legitimate regardless of age
        if (rows.length < 20) {
          continue; // SKIP - small legitimate batch
        }
        
        // ✅ WHITELIST 3: Medium batches (20-50 rows) under 24 hours = probably OK
        if (rows.length <= 50 && ageInHours < 24) {
          continue; // SKIP - recent medium-sized batch
        }
        
        // 🚩 Flag as SUSPICIOUS if:
        // - Old batch (> 7 days) with many rows = seed data
        // - OR very large batch (> 100 rows) at exact same millisecond
        // - OR medium batch (50-100) that's old (> 24 hours)
        
        let severity: 'critical' | 'high' | 'medium' = 'medium';
        let ageDescription = '';
        
        if (ageInDays > 7) {
          severity = 'high';
          ageDescription = `${ageInDays.toFixed(0)} days ago`;
        } else if (ageInHours > 24) {
          severity = 'medium';
          ageDescription = `${ageInHours.toFixed(0)} hours ago`;
        } else {
          ageDescription = `${ageInMinutes.toFixed(0)} minutes ago`;
        }
        
        // Increase severity for very large batches
        if (rows.length >= 100) {
          severity = severity === 'high' ? 'critical' : 'high';
        }
        
        results.push({
          pattern: 'identical_timestamps',
          description: `All ${timestamps.length} rows have identical "${col}" from ${ageDescription} (likely seed data)`,
          severity,
          examples: [String(timestamps[0])]
        });
      }
    }
  }

  return results;
}
